import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	OnInit,
	Output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	CodeInputComponent,
	IconComponent,
	InfoGroupComponent,
	LinkComponent,
	ModalComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { finalize, interval, startWith, Subscription, switchMap, take } from 'rxjs';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { isTfaSmsCodeRejected, ITfaSmsCodeResponse, PROCESS_FINISHED_ERROR_CODE, TWO_FACTOR_SEND_LIMIT_ERROR_CODE, TwoFactorChannel } from 'src/app/core/models/auth.models';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
	selector: 'app-two-factor-modal',
	imports: [
		ModalComponent,
		TranslatePipe,
		ReactiveFormsModule,
		TileComponent,
		CodeInputComponent,
		LinkComponent,
		IconComponent,
		InfoGroupComponent,
		AlertComponent,
	],
	templateUrl: './two-factor-modal.component.html',
	styleUrl: './two-factor-modal.component.scss',
})
export class TwoFactorModalComponent implements OnInit {
	/** Mismo valor que `process['2fa']` en la raíz (`sms` | `whatsapp`). */
	@Input({ required: true }) tfaType!: TwoFactorChannel;
	@Output() verified = new EventEmitter<void>();
	/** NIF-892: proceso caducado/terminado (1005) durante send o sms-code. */
	@Output() processFinished = new EventEmitter<void>();

	private readonly auth = inject(AuthService);
	private readonly trans = inject(TranslateService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly document = inject(DOCUMENT);
	private readonly host = inject(ElementRef<HTMLElement>);
	public readonly reactiveFormService = inject(ReactiveFormsService);

	/** Máximo de llamadas a `tfa/send` por operación (backend). */
	private static readonly MAX_TFA_SENDS = 3;
	/** Tras un fallo, no se lanzan nuevas peticiones hasta transcurridos al menos estos ms. */
	private static readonly MIN_RETRY_DELAY_MS = 5000;
	/** Intervalo de polling para WhatsApp (GET `tfa/:id/completed`). */
	private static readonly WHATSAPP_POLL_MS = 2000;

	readonly loading = signal(false);
	/** Reenvío SMS en curso: bloquea reloj, OTP y botón continuar. */
	readonly resendingSms = signal(false);
	readonly apiError = signal<string | null>(null);
	readonly countdown = signal('5:00');
	readonly smsVerified = signal(false);
	readonly whatsappVerified = signal(false);
	readonly showSms = signal(false);
	readonly showWhatsapp = signal(false);
	/** Mientras se espera confirmación en WhatsApp vía polling. */
	readonly whatsappPolling = signal(false);
	/** Indica que el backend pasó el envío a SMS tras fallo de entrega en WhatsApp (webhook). */
	readonly convertedToSms = signal(false);

	/** Validación del OTP solo al intentar enviar (botón primario o Enter), no al blur ni mientras escribe. */
	readonly otpForm = new FormGroup({
		otp: new FormControl(''),
	});

	private countdownSeconds = 300;
	private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
	/** Segundos guardados al pausar el countdown durante un reenvío SMS. */
	private pausedCountdownSeconds: number | null = null;
	/** Timestamp (ms) hasta el que no se permiten nuevas peticiones tras un error. */
	private requestCooldownUntilMs = 0;
	/** Identificadores devueltos por POST `tfa/send` por canal. */
	private tfaIdByChannel: Partial<Record<TwoFactorChannel, string>> = {};
	/** Número de envíos `tfa/send` realizados en esta sesión de modal. */
	private tfaSendCount = 0;
	private whatsappPollSub: Subscription | null = null;

	constructor() {
		const body = this.document.body;
		const prevOverflow = body.style.overflow;
		body.style.overflow = 'hidden';
		this.destroyRef.onDestroy(() => {
			body.style.overflow = prevOverflow;
			this.stopCountdown();
			this.stopWhatsappPolling();
		});
		this.otpForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.apiError.set(null);
			// Quitar error de obligatorio mostrado solo tras Enter/botón; el usuario está corrigiendo el campo.
			this.otpForm.get('otp')?.setErrors(null);
		});
	}

	public ngOnInit(): void {
		this.syncChannelVisibility();
		this.resetModalState();
		this.bootstrapSend();
	}

	private syncChannelVisibility(): void {
		const types = this.resolveTypes();
		this.showSms.set(types.includes('sms'));
		this.showWhatsapp.set(types.includes('whatsapp'));
	}

	private resolveTypes(): TwoFactorChannel[] {
		return [this.tfaType];
	}

	private resetModalState(): void {
		this.clearRequestCooldown();
		this.apiError.set(null);
		this.loading.set(false);
		this.resendingSms.set(false);
		this.pausedCountdownSeconds = null;
		this.otpForm.enable({ emitEvent: false });
		this.smsVerified.set(false);
		this.whatsappVerified.set(false);
		this.whatsappPolling.set(false);
		this.convertedToSms.set(false);
		this.otpForm.reset();
		this.stopCountdown();
		this.stopWhatsappPolling();
		this.tfaIdByChannel = {};
		this.tfaSendCount = 0;
		this.countdownSeconds = 300;
		this.countdown.set('5:00');
	}

	private stopCountdown(): void {
		if (this.countdownIntervalId !== null) {
			clearInterval(this.countdownIntervalId);
			this.countdownIntervalId = null;
		}
	}

	private startCountdown(fromSeconds: number = 300): void {
		this.stopCountdown();
		this.countdownSeconds = fromSeconds;
		this.countdown.set(this.formatTime(this.countdownSeconds));
		this.countdownIntervalId = setInterval(() => {
			this.countdownSeconds -= 1;
			if (this.countdownSeconds <= 0) {
				this.stopCountdown();
				this.countdown.set('0:00');
				return;
			}
			this.countdown.set(this.formatTime(this.countdownSeconds));
		}, 1000);
	}

	private pauseCountdownForResend(): void {
		this.pausedCountdownSeconds = this.countdownSeconds;
		this.stopCountdown();
	}

	private resumePausedCountdown(): void {
		if (this.pausedCountdownSeconds === null) {
			return;
		}
		const seconds = this.pausedCountdownSeconds;
		this.pausedCountdownSeconds = null;
		if (seconds <= 0) {
			this.countdown.set('0:00');
			return;
		}
		this.startCountdown(seconds);
	}

	private formatTime(totalSeconds: number): string {
		const m = Math.floor(totalSeconds / 60);
		const s = totalSeconds % 60;
		return `${m}:${s.toString().padStart(2, '0')}`;
	}

	private bootstrapSend(): void {
		if (this.blockIfInCooldown()) {
			return;
		}
		this.sendTfaRequest(this.tfaType);
	}

	/**
	 * Envía o reenvía el código (SMS o disparo WhatsApp). Máximo {@link TwoFactorModalComponent.MAX_TFA_SENDS} veces en total.
	 */
	private sendTfaRequest(channel: TwoFactorChannel): void {
		if (this.tfaSendCount >= TwoFactorModalComponent.MAX_TFA_SENDS) {
			return;
		}
		if (this.blockIfInCooldown()) {
			return;
		}
		const isSmsResend = channel === 'sms' && this.tfaSendCount > 0;
		if (isSmsResend) {
			this.pauseCountdownForResend();
			this.resendingSms.set(true);
			this.otpForm.disable({ emitEvent: false });
		}
		this.loading.set(true);
		this.auth
			.sendTfa(channel)
			.pipe(
				take(1),
				finalize(() => {
					this.loading.set(false);
					if (isSmsResend) {
						this.resendingSms.set(false);
						this.otpForm.enable({ emitEvent: false });
					}
				}),
			)
			.subscribe({
				next: (res) => {
					this.clearRequestCooldown();
					this.tfaIdByChannel[channel] = res.tfa_id;
					this.tfaSendCount += 1;
					if (channel === 'sms') {
						this.pausedCountdownSeconds = null;
						this.startCountdown();
					}
					if (channel === 'whatsapp') {
						this.startWhatsappPolling(res.tfa_id);
					}
				},
				error: (err: HttpErrorResponse) => {
					this.handleSendTfaError(err, isSmsResend);
				},
			});
	}

	private startWhatsappPolling(tfaId: string): void {
		this.stopWhatsappPolling();
		if (!tfaId) {
			return;
		}
		this.whatsappPolling.set(true);
		this.whatsappPollSub = interval(TwoFactorModalComponent.WHATSAPP_POLL_MS)
			.pipe(
				startWith(0),
				switchMap(() => this.auth.getTfaCompleted(tfaId)),
			)
			.subscribe({
				next: (res) => {
					if (res.expired) {
						this.stopWhatsappPolling();
						this.apiError.set(this.trans.instant('TWO_FACTOR.API_ERROR_GENERIC'));
						return;
					}
					if (res.converted_to_sms === true) {
						this.handleConvertedToSms();
						return;
					}
					if (res.completed) {
						this.stopWhatsappPolling();
						this.whatsappVerified.set(true);
						this.emitIfFullyVerified();
					}
				},
				error: (err: HttpErrorResponse) => {
					this.stopWhatsappPolling();
					this.handleRequestError(err);
				},
			});
	}

	private stopWhatsappPolling(): void {
		if (this.whatsappPollSub) {
			this.whatsappPollSub.unsubscribe();
			this.whatsappPollSub = null;
		}
		this.whatsappPolling.set(false);
	}

	/** WhatsApp no entregable: el servidor indica conversión a SMS; el usuario completa con OTP SMS. */
	private handleConvertedToSms(): void {
		this.convertedToSms.set(true);
		this.showSms.set(true);
		this.showWhatsapp.set(false);
		this.apiError.set(null);
		this.stopWhatsappPolling();
		this.startCountdown();
	}

	public canResendSms(): boolean {
		return this.tfaSendCount < TwoFactorModalComponent.MAX_TFA_SENDS && !this.smsVerified();
	}

	/** Backend ya no permite más envíos: ocultar reenvío y no mostrar error crudo del API. */
	private markTfaSendLimitReached(): void {
		this.tfaSendCount = TwoFactorModalComponent.MAX_TFA_SENDS;
		this.apiError.set(null);
		this.stopCountdown();
	}

	private handleSendTfaError(err: HttpErrorResponse, isSmsResend: boolean): void {
		if (this.emitIfProcessFinished(err)) {
			return;
		}
		if (this.isTfaSendLimitError(err)) {
			this.markTfaSendLimitReached();
			return;
		}
		if (isSmsResend) {
			this.resumePausedCountdown();
		}
		this.handleRequestError(err);
	}

	/** NIF-892: 1005 → cerrar modal y UX de caducado en el padre. */
	private emitIfProcessFinished(err: HttpErrorResponse): boolean {
		if (this.getHttpErrorCode(err) !== PROCESS_FINISHED_ERROR_CODE) {
			return false;
		}
		this.stopWhatsappPolling();
		this.stopCountdown();
		this.processFinished.emit();
		return true;
	}

	private isTfaSendLimitError(err: HttpErrorResponse): boolean {
		const code = this.getHttpErrorCode(err);
		if (code === TWO_FACTOR_SEND_LIMIT_ERROR_CODE) {
			return true;
		}
		const msg = this.mapHttpError(err).toLowerCase();
		return msg.includes('maximum verification code requests');
	}

	private getHttpErrorCode(err: HttpErrorResponse): number | null {
		const body = err.error as { error?: { error_code?: number } } | undefined;
		const code = body?.error?.error_code;
		return typeof code === 'number' ? code : null;
	}

	public resendSms(): void {
		if (!this.showSms() || this.loading() || this.resendingSms() || !this.canResendSms()) {
			return;
		}
		if (this.blockIfInCooldown()) {
			return;
		}
		this.sendTfaRequest('sms');
	}

	/**
	 * Enter con foco dentro del modal: mismo efecto que el botón de confirmar (envío OTP).
	 * Escucha a nivel documento para cubrir inputs dentro de `tec-code-input`.
	 */
	@HostListener('document:keydown.enter', ['$event'])
	protected onDocumentEnter(event: KeyboardEvent): void {
		if (!this.showSms() || this.loading() || this.resendingSms() || this.smsVerified()) {
			return;
		}
		const t = event.target;
		if (!t || !this.host.nativeElement.contains(t as Node)) {
			return;
		}
		event.preventDefault();
		this.submitSmsOtp();
	}

	public submitSmsOtp(): void {
		if (!this.showSms()) {
			return;
		}
		if (this.loading() || this.resendingSms()) {
			return;
		}
		const control = this.otpForm.get('otp');
		const raw = (control?.value ?? '').toString().trim();
		if (!raw) {
			control?.setErrors({ required: true });
			control?.markAsTouched();
			return;
		}
		control?.setErrors(null);
		if (!/^\d{6}$/.test(raw)) {
			this.apiError.set(this.trans.instant('TWO_FACTOR.OTP_INCOMPLETE'));
			return;
		}
		if (this.blockIfInCooldown()) {
			return;
		}
		this.loading.set(true);
		this.auth
			.submitSmsTfaCode({ otp: raw })
			.pipe(
				take(1),
				finalize(() => this.loading.set(false)),
			)
			.subscribe({
				next: (res: ITfaSmsCodeResponse) => {
					if (isTfaSmsCodeRejected(res)) {
						const serverMsg = res.error?.msg;
						if (typeof serverMsg === 'string' && serverMsg.length > 0) {
							this.apiError.set(serverMsg);
							return;
						}
						if (res.valid === false && res.expired === true) {
							this.apiError.set(this.trans.instant('TWO_FACTOR.OTP_EXPIRED'));
							return;
						}
						this.apiError.set(this.trans.instant('TWO_FACTOR.OTP_INVALID'));
						return;
					}
					this.clearRequestCooldown();
					this.stopCountdown();
					this.smsVerified.set(true);
					this.emitIfFullyVerified();
				},
				error: (err: HttpErrorResponse) => this.handleRequestError(err),
			});
	}

	public onModalClose(event: 'buttonPrimary' | 'buttonSecondary' | null | undefined): void {
		// NIF-893: la cruz / dismiss no cierran el modal (OTP obligatorio). Solo el primario verifica.
		if (event !== 'buttonPrimary' || this.loading() || this.resendingSms()) {
			return;
		}

		if (this.showSms() && !this.smsVerified()) {
			this.submitSmsOtp();
			return;
		}

		/** WhatsApp se resuelve por polling; el botón primario no aplica. */
	}

	private emitIfFullyVerified(): void {
		if (this.convertedToSms()) {
			if (this.smsVerified()) {
				this.verified.emit();
			}
			return;
		}
		const types = this.resolveTypes();
		if (types.includes('sms') && !this.smsVerified()) {
			return;
		}
		if (types.includes('whatsapp') && !this.whatsappVerified()) {
			return;
		}
		this.verified.emit();
	}

	private mapHttpError(err: HttpErrorResponse): string {
		const body = err.error as
			| {
					message?: string;
					msg?: string;
					error?: string | { msg?: string; error_code?: number };
			  }
			| undefined;
		const nested = typeof body?.error === 'object' && body.error ? body.error.msg : undefined;
		const flat = body?.message ?? (typeof body?.error === 'string' ? body.error : undefined) ?? nested ?? body?.msg;
		if (typeof flat === 'string' && flat.length > 0) {
			return flat;
		}
		return this.trans.instant('TWO_FACTOR.API_ERROR_GENERIC');
	}

	private clearRequestCooldown(): void {
		this.requestCooldownUntilMs = 0;
	}

	/** @returns true si debe bloquearse la petición (y ya se ha mostrado mensaje). */
	private blockIfInCooldown(): boolean {
		const now = Date.now();
		if (now < this.requestCooldownUntilMs) {
			const remainingSec = Math.ceil((this.requestCooldownUntilMs - now) / 1000);
			this.apiError.set(this.trans.instant('TWO_FACTOR.RETRY_WAIT', { seconds: remainingSec }));
			return true;
		}
		return false;
	}

	private registerRequestFailure(): void {
		this.requestCooldownUntilMs = Date.now() + TwoFactorModalComponent.MIN_RETRY_DELAY_MS;
	}

	private handleRequestError(err: HttpErrorResponse): void {
		if (this.emitIfProcessFinished(err)) {
			return;
		}
		this.registerRequestFailure();
		this.apiError.set(this.mapHttpError(err));
	}
}
